import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2, Shield, ClipboardCheck, Plus, CalendarCheck, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { Location } from "@/lib/locationContext";
import type { LocationStatus } from "./LocationCard";
import CompareTab from "./CompareTab";
import SectionEmptyState from "@/components/SectionEmptyState";

interface LocationStats {
  permitCount: number;
  expiringSoon: number;
  inspectionChecked: number;
  inspectionTotal: number;
}

interface LocationDetailPanelProps {
  location: Location;
  status: LocationStatus;
  score: number;
  stats: LocationStats | null;
  allLocations: Location[];
  allScores: Record<string, number>;
  allStatuses: Record<string, LocationStatus>;
  onEdit: () => void;
  onDelete: () => void;
}

export default function LocationDetailPanel({
  location, status, score, stats, allLocations, allScores, allStatuses, onEdit, onDelete,
}: LocationDetailPanelProps) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const inspPct = stats ? Math.round((stats.inspectionChecked / stats.inspectionTotal) * 100) : 0;

  const statusLabel = status === "healthy" ? "Healthy" : status === "at-risk" ? "At Risk" : "Pre-Opening";
  const statusClasses = status === "healthy" ? "bg-primary/10 text-primary" : status === "at-risk" ? "bg-destructive/10 text-destructive" : "bg-accent/10 text-accent";
  const scoreColor = score >= 80 ? "text-primary" : score >= 60 ? "text-accent" : "text-destructive";

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-medium text-foreground truncate">{location.name}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {[location.business_type, location.city, location.address].filter(Boolean).join(" · ")}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${statusClasses}`}>{statusLabel}</span>
            <span className={`text-sm font-medium ${scoreColor}`}>{score}%</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
            <Edit2 className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDelete}>
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
        <div className="border-b border-border px-4">
          <TabsList className="bg-transparent h-9 gap-4 p-0">
            <TabsTrigger value="overview" className="text-xs px-0 pb-2 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">Overview</TabsTrigger>
            <TabsTrigger value="tasks" className="text-xs px-0 pb-2 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">Tasks</TabsTrigger>
            <TabsTrigger value="insights" className="text-xs px-0 pb-2 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">AI Insights</TabsTrigger>
            <TabsTrigger value="compare" className="text-xs px-0 pb-2 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">Compare</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <TabsContent value="overview" className="mt-0 space-y-4">
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Permits</span>
                  </div>
                  <p className="text-lg font-medium text-foreground">{stats.permitCount}</p>
                  {stats.expiringSoon > 0 && (
                    <p className="text-[10px] text-destructive font-medium">{stats.expiringSoon} expiring soon</p>
                  )}
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ClipboardCheck className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Inspection</span>
                  </div>
                  <p className="text-lg font-medium text-foreground">{inspPct}%</p>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                    <div
                      className={`h-full rounded-full ${inspPct >= 80 ? "bg-primary" : inspPct >= 50 ? "bg-accent" : "bg-destructive"}`}
                      style={{ width: `${inspPct}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Revenue Breakdown */}
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-foreground uppercase tracking-wider">Revenue Breakdown</h3>
              <SectionEmptyState
                icon={DollarSign}
                title="No revenue data yet"
                description="Log your first monthly check-in to see revenue, labor, food cost, and rent breakdowns."
                actionLabel="Log Monthly Check-In"
                onAction={() => navigate("/dashboard", { state: { tab: "operations", section: "checkin" } })}
              />
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="mt-0">
            <div className="text-center py-8">
              <ClipboardCheck className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Task tracking coming soon.</p>
              <p className="text-xs text-muted-foreground mt-1">Your timeline tasks will be linked to each location.</p>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="mt-0">
            <div className="text-center py-8">
              <Plus className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">AI Insights will appear after your first check-in.</p>
            </div>
          </TabsContent>

          <TabsContent value="compare" className="mt-0">
            <CompareTab
              currentLocation={location}
              allLocations={allLocations}
              allScores={allScores}
              allStatuses={allStatuses}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
